// eslint-disable-next-line no-restricted-imports
import { Trans } from '@lingui/react';
import Grid from '@material-ui/core/Grid';
import ActiveGlobe from 'components/ActiveGlobe';
import HeaderSpacing from 'components/HeaderSpacing';
import LocalCities from 'components/LocalCities';
import PageMeta from 'components/PageMeta';
import SectionContainer from 'components/SectionContainer';
import SectionTitle from 'components/SectionTitle';
import StudioListing, { Studios } from 'components/StudioListing';
import TeacherTile from 'components/TeacherTile';
import VerticalSpacing from 'components/VerticalSpacing';
import Footer from 'components_2/footer';
import PageNotFound from 'components_2/pageNotFound';
import isNumberValue from 'helpers/isNumberValue';
import useLocals from 'hooks/useLocals';
import map from 'lodash/map';
import React, { FunctionComponent, useEffect, useState } from 'react';
import { RouteComponentProps } from 'react-router';
import { UserSummary } from 'services/courses';
import useFilterPlaylists from 'views_2/Playlists/hooks/useFilterPlaylists';
import CoursesByLocalPublisherCarousel from './CoursesByLocalPublisherCarousel';
import { usePublishersLocal } from './hooks';
import LocalGroupsContainer from './LocalGroupsContainer';
import LocalPlaylists from './LocalPlaylists';
import LocalPopularCoursesCarousel from './LocalPopularCoursesCarousel';
import {
  GlobeContainer,
  LinkPosition,
  PageContainer,
  PageDescription,
  SectionCarouselContainer,
  SeeAllLink,
  Title,
  TitleSection
} from './styles';

type TProps = RouteComponentProps<{
  country?: string;
  city?: string;
}>;

const Local: FunctionComponent<TProps> = ({ match }) => {
  const [localStudios, setLocalStudios] = useState<Studios[]>();
  const countryCode = match.params.country;
  const citySlug = match.params.city;
  const { loading, locals, getRandomCities } = useLocals();

  const countryInfo =
    locals && locals.find(local => local.isoCode === countryCode);
  const cityInfo =
    countryInfo && countryInfo.cities.find(city => city.slug === citySlug);
  const lat = parseFloat(`${cityInfo?.lat}`);
  const lon = parseFloat(`${cityInfo?.lon}`);
  const isLatLonInNumFormat = isNumberValue(lat) && isNumberValue(lon);
  const { data } = usePublishersLocal(
    cityInfo && cityInfo.lat.toString(),
    cityInfo && cityInfo.lon.toString()
  );

  const randomCities = getRandomCities();

  const regionCity = cityInfo && cityInfo.slug.split('-').join(',');
  const { list } = useFilterPlaylists({
    offset: 0,
    limit: 8,
    region_name: regionCity,
    sort_option: 'popular',
    sort_direction: 'desc'
  });

  const getSeeAllLinkText = (
    total_count: number,
    cityName?: string,
    countryName?: string
  ) => {
    if (cityName && countryName)
      return `See all ${total_count.toLocaleString()} Meditation Teachers in ${cityName}, ${countryName}`;
    if (cityName)
      return `See all ${total_count.toLocaleString()} Meditation Teachers in ${cityName}`;
    return `See all ${total_count.toLocaleString()} Meditation Teachers`;
  };

  useEffect(() => {
    const getLocalStudios = async () => {
      try {
        const studiosDetails: { default: Studios[] } = await import(
          `assets_2/static/local_studios/${citySlug}.json`
        );

        setLocalStudios(studiosDetails?.default ?? []);
      } catch (err) {
        console.error('caught error reading studios');
      }
    };

    getLocalStudios();
  }, [citySlug]);

  const teacherIds = data && map(data.data.slice(0, 8), 'id');

  if (!loading && !cityInfo) return <PageNotFound />;

  const title = `Meditation in ${cityInfo && cityInfo.name} | Insight Timer`;
  const description = `Thousands of people in ${
    cityInfo && cityInfo.name
  } are meditating right now. Meditate with popular local teachers, join groups, and find local meditation retreats.`;

  return (
    <>
      <PageMeta
        title={title}
        description={description}
        url={`https://insighttimer.com/local/${countryCode}/${citySlug}`}
      />
      <PageContainer>
        <GlobeContainer>
          <SectionContainer>
            {cityInfo && isLatLonInNumFormat && (
              <ActiveGlobe lat={lat} lon={lon} name={cityInfo.name} />
            )}
          </SectionContainer>
        </GlobeContainer>

        <SectionContainer>
          <HeaderSpacing />
          {cityInfo && (
            <TitleSection>
              <Title>
                Meditation in
                <br />
                {cityInfo && cityInfo.name}
              </Title>

              <PageDescription>
                Thousands of people in {cityInfo && cityInfo.name} are
                meditating right now. Meditate with popular local teachers, join
                groups, and find local meditation retreats near you.
              </PageDescription>
            </TitleSection>
          )}
        </SectionContainer>

        <VerticalSpacing height={30} />

        <div style={{ height: '250vh' }} />

        {data && data.data.length > 0 && (
          <SectionContainer variant="small">
            <SectionTitle>
              Meditation teachers in {cityInfo && cityInfo.name}
            </SectionTitle>

            <VerticalSpacing height={30} />
            <Grid container direction="row" alignItems="center" spacing={3}>
              {data.data.map((teacher: UserSummary) => (
                <Grid
                  item
                  key={teacher.id}
                  className="w-1/2 xs:w-1/3 sm:w-1/3 md:w-1/4"
                >
                  <TeacherTile
                    to={`/${teacher.username}`}
                    id={teacher.id || ''}
                    name={teacher.name || ''}
                    location={teacher.region?.name || ''}
                    lazyLoadInView
                  />
                </Grid>
              ))}
            </Grid>
            <VerticalSpacing height={[40, 80]} />
            <LinkPosition>
              <SeeAllLink
                href={`/local/${countryCode}/${citySlug}/meditation-teachers`}
              >
                {getSeeAllLinkText(
                  data.total_count,
                  cityInfo && cityInfo.name,
                  countryInfo && countryInfo.name
                )}
              </SeeAllLink>
            </LinkPosition>
          </SectionContainer>
        )}

        {/* carousels */}
        {teacherIds && (
          <>
            <VerticalSpacing height={[50, 100]} />

            <SectionCarouselContainer>
              <CoursesByLocalPublisherCarousel teacherIds={teacherIds}>
                <Trans
                  id="Meditation Courses by {city} Teachers"
                  values={{ city: cityInfo && cityInfo.name }}
                />
                <VerticalSpacing height={[10, 5]} />
              </CoursesByLocalPublisherCarousel>
            </SectionCarouselContainer>
          </>
        )}

        {cityInfo && isLatLonInNumFormat ? (
          <>
            <VerticalSpacing height={30} />
            <SectionCarouselContainer>
              <LocalGroupsContainer lat={lat} lon={lon} city={cityInfo.name} />
            </SectionCarouselContainer>
          </>
        ) : null}

        <VerticalSpacing height={30} />

        <SectionCarouselContainer>
          <LocalPopularCoursesCarousel>
            <Trans id="Virtual Meditation Classes Online" />
            <VerticalSpacing height={[10, 5]} />
          </LocalPopularCoursesCarousel>
        </SectionCarouselContainer>

        <VerticalSpacing height={30} />

        {list && list.length > 0 && (
          <SectionContainer variant="small">
            <SectionTitle>Meditation Playlists curated by locals</SectionTitle>

            <VerticalSpacing height={30} />

            {list?.length > 0 && <LocalPlaylists playlists={list} />}
          </SectionContainer>
        )}

        {localStudios && localStudios.length > 0 && (
          <>
            <VerticalSpacing height={[50, 90]} />

            <SectionContainer variant="small">
              <StudioListing
                title={`Meditation Studios & Retreat Centres in ${
                  cityInfo ? cityInfo.name : ''
                }`}
                studios={localStudios}
              />
            </SectionContainer>
          </>
        )}

        <VerticalSpacing height={[50, 90]} />

        <SectionContainer variant="small">
          <LocalCities title="Other Cities" cities={randomCities} />
        </SectionContainer>

        <VerticalSpacing height={[100, 120]} />
      </PageContainer>

      <Footer />
    </>
  );
};

export default Local;
